import { useEffect, useMemo, useState } from 'react';
import { History } from 'lucide-react';
import { format, formatDistanceToNowStrict } from 'date-fns';
import { useApi } from '../contexts/ApiContext';
import type { ApiResponse } from '../types';
import { Skeleton } from './ui/Skeleton';

type Category = 'registrar' | 'renewal' | 'nameservers' | 'site' | 'whois' | 'edit' | 'alert' | 'account';
interface HistoryEvent {
  id: string;
  at: string;
  category: Category;
  tone: 'ok' | 'warn' | 'bad' | 'info';
  title: string;
  detail: string | null;
  actor: string;
}

const CATEGORY_LABEL: Record<Category, string> = {
  registrar: 'Registrar', renewal: 'Renewals', nameservers: 'Nameservers', site: 'Site', whois: 'WHOIS', edit: 'Edits', alert: 'Alerts', account: 'Account',
};
const DOT: Record<HistoryEvent['tone'], string> = { ok: 'bg-green-500', warn: 'bg-amber-400', bad: 'bg-red-500', info: 'bg-slate-300' };
const PAGE = 25;

/** Everything that happened to one domain, newest first, filterable by kind. */
export default function DomainHistory({ domainId }: { domainId: string }) {
  const { fetchApi } = useApi();
  const [events, setEvents] = useState<HistoryEvent[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Category | 'all'>('all');
  const [shown, setShown] = useState(PAGE);

  useEffect(() => {
    setEvents(null);
    fetchApi<ApiResponse<HistoryEvent[]>>(`/domains/${domainId}/history`)
      .then((r) => setEvents(r.data ?? []))
      .catch((e) => setError(e instanceof Error ? e.message : 'Could not load history'));
  }, [fetchApi, domainId]);

  const counts = useMemo(() => {
    const m = new Map<Category, number>();
    for (const e of events ?? []) m.set(e.category, (m.get(e.category) ?? 0) + 1);
    return m;
  }, [events]);
  const list = (events ?? []).filter((e) => filter === 'all' || e.category === filter);

  return (
    <div className="card mt-6 overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 px-5 py-4">
        <h2 className="flex items-center gap-2 text-base font-semibold text-slate-900"><History className="h-4 w-4 text-slate-400" />History</h2>
        {events && events.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {(['all', ...counts.keys()] as (Category | 'all')[]).map((k) => (
              <button key={k} type="button" onClick={() => { setFilter(k); setShown(PAGE); }} aria-pressed={filter === k}
                className={`rounded-full border px-2 py-0.5 text-xs ${filter === k ? 'border-slate-400 bg-slate-100 text-slate-900' : 'border-slate-200 text-slate-500 hover:text-slate-700'}`}>
                {k === 'all' ? 'All' : CATEGORY_LABEL[k]} <span className="tabular-nums text-slate-400">{k === 'all' ? events.length : counts.get(k)}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="px-5 py-4">
        {error ? <p className="text-sm text-red-600">{error}</p>
          : !events ? <div className="space-y-2"><Skeleton className="h-4 w-1/2" /><Skeleton className="h-4 w-2/3" /><Skeleton className="h-4 w-1/3" /></div>
          : list.length === 0 ? <p className="text-sm text-slate-500">Nothing recorded yet.</p>
          : (
            <>
              <ol className="relative ml-1 space-y-3 border-l border-slate-200 pl-4">
                {list.slice(0, shown).map((e) => (
                  <li key={e.id} className="relative">
                    <span className={`absolute -left-[21px] top-1.5 h-2.5 w-2.5 rounded-full border-2 border-surface ${DOT[e.tone]}`} />
                    <div className="flex flex-wrap items-baseline justify-between gap-x-3">
                      <span className="text-sm font-medium text-slate-800">{e.title}</span>
                      <span className="text-xs text-slate-400" title={format(new Date(e.at), 'PPpp')}>
                        {formatDistanceToNowStrict(new Date(e.at), { addSuffix: true })} · {format(new Date(e.at), 'd MMM yyyy')}
                      </span>
                    </div>
                    {e.detail && <p className="break-words text-sm text-slate-600">{e.detail}</p>}
                    <p className="text-xs text-slate-400">{CATEGORY_LABEL[e.category]} · {e.actor}</p>
                  </li>
                ))}
              </ol>
              {list.length > shown && (
                <button type="button" onClick={() => setShown((n) => n + PAGE)} className="mt-3 text-sm font-medium text-brand-600 hover:underline">
                  Show {Math.min(PAGE, list.length - shown)} more
                </button>
              )}
            </>
          )}
      </div>
    </div>
  );
}
